import { useState } from "react";
import { Flag, Hammer } from "lucide-react";

import { Counted, ListCard, ListEmpty, ListRow } from "@/shared/components/ListCard";
import { Segmented } from "@/shared/components/teams-ui";
import { BrandButton, Chip, GhostButton, PageHeader } from "@/shared/components/ui-kit";
import { ago } from "@/shared/lib/time";
import { useReports, useSetReportStatus } from "./api/reports";

import type { Agent, AgentReport, ReportKind, ReportStatus } from "@/shared/types";

const KIND: Record<ReportKind, { label: string; tone: "warn" | "danger" | "neutral" }> = {
  missing_capability: { label: "Missing capability", tone: "warn" },
  unclear_instructions: { label: "Unclear instructions", tone: "neutral" },
  failure: { label: "Failure", tone: "danger" },
};

/* What the agent told Builder it couldn't do for someone (its `report_to_builder`
   calls). Builder never acts on one by itself: a person opens it here and asks
   Builder to fix it, which drafts a change they publish. The summary was written
   while the agent talked to a customer, so it is shown as it is — evidence. */
export default function ReportsPage({ agent, onFixWithBuilder }: {
  agent?: Agent | null;
  /** Opens Builder's chat with the report as its first message. */
  onFixWithBuilder: (report: AgentReport) => void;
}) {
  const [status, setStatus] = useState<ReportStatus>("open");
  const reports = useReports(agent?.id ?? "", status);
  const setReport = useSetReportStatus(agent?.id ?? "");
  if (!agent) return null;
  const rows = reports.data ?? [];

  return (
    <div className="flex-1 min-w-0 flex flex-col min-h-0">
      <PageHeader title="Reports">
        <span className="hidden sm:inline text-[12.5px] text-[var(--text-tertiary)] truncate">
          What {agent.name} told Builder it couldn't do
        </span>
      </PageHeader>
      <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-5">
        <div className="max-w-[760px] mx-auto">
          <ListCard
            label={reports.isPending ? "Loading reports…" : <Counted n={rows.length} noun={`${status} report`} />}
            action={
              <Segmented
                value={status}
                onChange={setStatus}
                options={[{ value: "open", label: "Open" }, { value: "resolved", label: "Resolved" }]}
              />
            }
          >
            {!reports.isPending && rows.length === 0 && (
              <ListEmpty
                icon={Flag}
                title={status === "open" ? "Nothing to fix" : "Nothing resolved yet"}
                hint="When the agent can't do what someone asks, it tells Builder, and the report shows here."
              />
            )}
            {rows.map((report) => (
              <ListRow
                key={report.id}
                lead={<Chip tone={KIND[report.kind].tone}>{KIND[report.kind].label}</Chip>}
                title={report.summary}
                desc={[report.needed && `Needed: ${report.needed}`, ago(report.created_at)].filter(Boolean).join(" · ")}
              >
                {report.status === "open" && (
                  <BrandButton onClick={() => onFixWithBuilder(report)} className="h-7 px-2.5">
                    <Hammer className="w-3 h-3" strokeWidth={1.75} /> Fix with Builder
                  </BrandButton>
                )}
                <GhostButton
                  onClick={() => setReport.mutate({ id: report.id, status: report.status === "open" ? "resolved" : "open" })}
                  className="h-7 px-2.5"
                >
                  {report.status === "open" ? "Resolve" : "Reopen"}
                </GhostButton>
              </ListRow>
            ))}
          </ListCard>
        </div>
      </div>
    </div>
  );
}
